import { useApolloClient } from "@apollo/client";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { useDuplicateQuoteMutation } from "../buttons/DuplicateQuoteButton.generated.js";

/**
 * Copies a quote into a new draft quote in the same project. Resolves to the
 * copy's id, or to `null` after showing an error snackbar.
 */
export function useDuplicateQuote({
  projectId,
  quoteId,
}: {
  projectId: string;
  quoteId: string;
}) {
  const { t } = useTranslate("Global");
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [duplicateQuote, { loading }] = useDuplicateQuoteMutation({ client });

  const duplicate = async (): Promise<string | null> => {
    try {
      const result = await duplicateQuote({
        variables: { input: { projectId, quoteId } },
      });
      const newQuoteId = result.data?.copyQuoteIntoNewQuote.quote.id;
      if (newQuoteId) return newQuoteId;

      enqueueSnackbar(
        t("Something went wrong. Please try again.", { ns: "Global" }),
        { variant: "error" }
      );
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
    return null;
  };

  return { duplicate, loading };
}
